import type { EditorAction } from "../../editor/reducers";
import type { Dispatch } from "react";
import type { FormulaRegionCreationTabFragment } from "../../client/generated";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { NavbarTabCloseButton } from "../../shell/NavbarTabCloseButton";
import { NavbarTabContent } from "../../shell/NavbarTabContent";
import { useTranslation } from "react-i18next";
import { makeTabIdentifier } from "../../editor/reducers";
import { useState } from "react";
import { assertTypeName } from "../../client/guards";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { ExpressionReferenceFormField } from "../../attributes/expression/ExpressionReferenceFormField";
import { ExpressionPreviewFormField } from "../../attributes/expression/ExpressionPreviewFormField";
import { ExpressionFormField } from "../../attributes/expression/ExpressionFormField";
import {
  useCreateFormulaRegionMutation,
  useFormulaRegionCreationTabSuspenseQuery,
} from "../../client/generated";

type Props = {
  fragment: FormulaRegionCreationTabFragment;
  dispatch: Dispatch<EditorAction>;
};

export function FormulaRegionCreationTab({ fragment, dispatch }: Props) {
  const { t } = useTranslation();

  return (
    <NavbarTabContent
      value={makeTabIdentifier("formula", fragment.id)}
      title={t("FormulaRegionCreationTab.title")}
      right={<NavbarTabCloseButton dispatch={dispatch} />}
    >
      <ScrollArea className="flex-1">
        <Body fragment={fragment} dispatch={dispatch} />
      </ScrollArea>
    </NavbarTabContent>
  );
}

function Body({ fragment, dispatch }: Props) {
  const expressionAttributeIds = fragment.attributes
    .filter((attr) => attr.__typename === "ExpressionAttribute")
    .map((attr) => attr.id);

  const { data } = useFormulaRegionCreationTabSuspenseQuery({
    variables: {
      regionId: fragment.id,
      expressionAttributeIds,
    },
  });

  assertTypeName(data.region?.__typename === "Region");

  const [createRegion] = useCreateFormulaRegionMutation();

  const [expressionLatex, setExpressionLatex] = useState(() => {
    const attr = data.expressionAttributes[0];
    if (!attr) return undefined;
    assertTypeName(attr.__typename === "ExpressionAttribute");
    return attr.expressionLatex;
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (expressionLatex === undefined) return false;

    const result = await createRegion({
      variables: {
        regionId: fragment.id,
        expressionLatex,
        excessAttributeIds: fragment.attributes.map((attr) => attr.id),
      },
      update: (cache) => {
        for (const attr of fragment.attributes) {
          const id = cache.identify(attr);
          cache.evict({ id });
          cache.gc();
        }
      },
    });

    return (
      !result.errors &&
      !result.data?.createExpressionAttribute.userErrors?.length &&
      !result.data?.deleteAttributes.userErrors?.length
    );
  };

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <ExpressionReferenceFormField fragment={data.region} />
      <ExpressionPreviewFormField latex={expressionLatex ?? ""} />
      <ExpressionFormField
        fragment={data.region}
        value={expressionLatex}
        onChange={(event) => setExpressionLatex(event.target.value)}
        onPrediction={setExpressionLatex}
      />
    </RegionUpdateForm>
  );
}
